import { useEffect, useState } from 'react';
import { useToast } from '../../../components/ToastContext';

const emptyForm = {
  title: '',
  description: '',
  offer_type: 'EARLY_BIRD',
  discount_type: 'PERCENT',
  discount_value: 5,
  min_nights: 1,
  booking_window_days: '',
  stay_start_date: '',
  stay_end_date: '',
  priority: 1,
  is_active: true,
};

const SpecialOffersTab = ({ accessToken, propertyId, listFn, saveFn, deleteFn }) => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const toast = useToast();

  const load = async () => {
    if (!propertyId) return;
    setError('');
    try {
      const rows = await listFn(accessToken, propertyId);
      setItems(rows || []);
    } catch (err) {
      setError(err.message || 'Failed to load offers');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!propertyId) {
      toast.error('Select a property first');
      return;
    }
    try {
      const payload = { ...form, property_id: propertyId };
      if (!editingId) delete payload.id;
      const saved = await saveFn(accessToken, editingId ? { ...payload, id: editingId } : payload);
      setItems((prev) => {
        const filtered = prev.filter((i) => i.id !== saved.id);
        return [saved, ...filtered];
      });
      setForm(emptyForm);
      setEditingId(null);
      toast.success('Saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      ...emptyForm,
      ...row,
    });
  };

  const remove = async (row) => {
    if (!window.confirm('Delete this offer?')) return;
    await deleteFn(accessToken, row.id);
    setItems((prev) => prev.filter((i) => i.id !== row.id));
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div>
          <p className="text-lg font-semibold text-slate-900">Special offers</p>
          <p className="text-sm text-slate-600">Automatic deals like early-bird, last-minute, long stay.</p>
        </div>
        <button className="text-xs text-emerald-700 underline" onClick={load}>
          Refresh
        </button>
      </div>
      {error && <div className="px-4 py-2 text-sm text-red-600">{error}</div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
        <form onSubmit={submit} className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-slate-900">{editingId ? 'Edit offer' : 'Create offer'}</p>
              <p className="text-xs text-slate-500">Offer applies automatically when criteria match.</p>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
              Active
            </label>
          </div>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Title</span>
            <input
              required
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              placeholder="Early bird"
            />
          </label>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Description</span>
            <textarea
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              rows={2}
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Offer type</span>
              <select
                value={form.offer_type}
                onChange={(e) => setForm((f) => ({ ...f, offer_type: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              >
                <option value="EARLY_BIRD">Early bird</option>
                <option value="LAST_MINUTE">Last minute</option>
                <option value="WEEKLY">Weekly</option>
                <option value="MONTHLY">Monthly</option>
                <option value="LONG_STAY">Long stay</option>
                <option value="FESTIVE">Festive</option>
                <option value="GENERAL">General</option>
              </select>
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Discount</span>
              <div className="flex gap-2">
                <select
                  value={form.discount_type}
                  onChange={(e) => setForm((f) => ({ ...f, discount_type: e.target.value }))}
                  className="rounded-lg border border-slate-200 px-3 py-2"
                >
                  <option value="PERCENT">Percent</option>
                  <option value="FIXED">Fixed</option>
                </select>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={form.discount_value}
                  onChange={(e) => setForm((f) => ({ ...f, discount_value: e.target.value }))}
                  className="w-24 rounded-lg border border-slate-200 px-3 py-2"
                />
              </div>
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Min nights</span>
              <input
                type="number"
                min="1"
                value={form.min_nights}
                onChange={(e) => setForm((f) => ({ ...f, min_nights: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Booking window days</span>
              <input
                type="number"
                min="0"
                value={form.booking_window_days}
                onChange={(e) => setForm((f) => ({ ...f, booking_window_days: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
                placeholder="e.g. 30 for early bird"
              />
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Stay date range (optional)</span>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={form.stay_start_date}
                  onChange={(e) => setForm((f) => ({ ...f, stay_start_date: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2"
                />
                <input
                  type="date"
                  value={form.stay_end_date}
                  onChange={(e) => setForm((f) => ({ ...f, stay_end_date: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2"
                />
              </div>
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Priority</span>
              <input
                type="number"
                min="1"
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
              {editingId ? 'Update offer' : 'Create offer'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm(emptyForm);
                }}
                className="text-sm text-slate-600 underline"
              >
                Cancel edit
              </button>
            )}
          </div>
        </form>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div>
              <p className="text-lg font-semibold text-slate-900">Offers</p>
              <p className="text-xs text-slate-500">Auto-applied deals by rules.</p>
            </div>
          </div>
          <div className="divide-y divide-slate-100">
            {items.length === 0 && <div className="p-4 text-sm text-slate-600">No offers yet.</div>}
            {items.map((o) => (
              <div key={o.id} className="flex items-start justify-between px-4 py-3 hover:bg-slate-50">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900">{o.title || `Offer #${o.id}`}</p>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">{o.offer_type || 'GENERAL'}</span>
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                      {o.discount_type} {o.discount_value}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">
                    Min nights {o.min_nights || 1}
                    {o.booking_window_days ? ` • book ${o.booking_window_days}+ days ahead` : ''}
                  </p>
                  {(o.stay_start_date || o.stay_end_date) && (
                    <p className="text-xs text-slate-500">
                      Stay window {o.stay_start_date || '—'} to {o.stay_end_date || '—'}
                    </p>
                  )}
                  {o.description && <p className="text-sm text-slate-700">{o.description}</p>}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <label className="flex items-center gap-1 text-slate-600">
                    <input
                      type="checkbox"
                      checked={!!o.is_active}
                      onChange={(e) => saveFn(accessToken, { ...o, is_active: e.target.checked }).then(load)}
                    />
                    Active
                  </label>
                  <button className="text-emerald-700 underline" onClick={() => startEdit(o)}>
                    Edit
                  </button>
                  <button className="text-red-600 underline" onClick={() => remove(o)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpecialOffersTab;
